// src/App.js

import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useParams, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import ProtectedRoute from './components/ProtectedRoute';
import AppErrorBoundary from './components/AppErrorBoundary';
import VolugraphLoadingScreen from './components/VolugraphLoadingScreen';
import ShopifyLoadingScreen from './components/Shopify/ShopifyLoadingScreen';
import { Toaster } from 'react-hot-toast';
import volugraphToastFavicon from './assets/icons/Favicon High Res.jpg';
import { useAuth } from './context/AuthContext';
import { UploadProvider } from './context/UploadContext';
import { ContactModalProvider, useContactModal } from './context/modalDisclosureContexts';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { ProjectProvider } from './context/ProjectContext';
import { StudioMetricsProvider } from './hooks/studioMetrics';
import CookieConsent from './components/CookieConsent';
import SeoAnalytics from './components/SeoAnalytics';
import UserBilling from './Pages/Studio/Billing';
import UserSettings from './Pages/Studio/Settings';

const LAZY_RELOAD_FLAG_KEY = 'starling_lazy_reload_once';
const CHUNK_LOAD_ERROR_RE = /ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module/i;

function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      const mod = await importer();
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem(LAZY_RELOAD_FLAG_KEY);
        } catch {}
      }
      return mod;
    } catch (error) {
      const message = error?.message || String(error || '');
      const isChunkLoadError = CHUNK_LOAD_ERROR_RE.test(message);
      if (isChunkLoadError && typeof window !== 'undefined') {
        let hasReloaded = false;
        try {
          hasReloaded = sessionStorage.getItem(LAZY_RELOAD_FLAG_KEY) === '1';
        } catch {}
        if (!hasReloaded) {
          try {
            sessionStorage.setItem(LAZY_RELOAD_FLAG_KEY, '1');
          } catch {}
          window.location.reload();
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });
}

// Route-level code splitting – heavy pages load on demand
const Landing = lazyWithRetry(() => import('./Pages/Landing/Landing'));
const Pricing = lazyWithRetry(() => import('./Pages/Pricing/Pricing'));
const UserLayout = lazyWithRetry(() => import('./Pages/Studio/Layout'));
const UserOutlet = lazyWithRetry(() => import('./Pages/Studio/Layout/UserOutlet'));
const UserCaptures = lazyWithRetry(() => import('./Pages/Studio/Projects'));
const CaptureViewer = lazyWithRetry(() => import('./Pages/Studio/CaptureViewer'));
const Login = lazyWithRetry(() => import('./Pages/Login/Login'));
const Privacy = lazyWithRetry(() => import('./Pages/Privacy/Privacy'));
const Terms = lazyWithRetry(() => import('./Pages/Terms/Terms'));
const ContactModal = lazyWithRetry(() => import('./Pages/ContactModal'));
const DocumentationLayout = lazyWithRetry(() => import('./Pages/Documentation/DocumentationLayout'));
const DocumentationHome = lazyWithRetry(() => import('./Pages/Documentation/DocumentationHome'));
const DocumentationCategoryPage = lazyWithRetry(() => import('./Pages/Documentation/DocumentationCategoryPage'));
const DocumentationOfficialArticle = lazyWithRetry(() => import('./Pages/Documentation/DocumentationOfficialArticle'));
const DocumentationArticle = lazyWithRetry(() => import('./Pages/Documentation/DocumentationArticle'));
/** Single object so eslint/webpack marks lazy entries as used via property access + JSX */
const LazyBlog = {
  Index: lazyWithRetry(() => import('./Pages/Blog/BlogIndex')),
  Post: lazyWithRetry(() => import('./Pages/Blog/BlogPostPage')),
};
const ComingSoon = lazyWithRetry(() => import('./Pages/ComingSoon/ComingSoon'));
const About = lazyWithRetry(() => import('./Pages/About/About'));
/** Single object so eslint/webpack mark lazy AcceptProjectInvitePage as used (property + JSX). */
const LazyInvite = {
  AcceptProjectInvitePage: lazyWithRetry(() => import('./Pages/Invite/AcceptProjectInvitePage')),
};
const ShareView = lazyWithRetry(() => import('./Pages/ShareView'));
const EmbedCapture = lazyWithRetry(() => import('./Pages/EmbedCapture'));
const EmbedShareCapture = lazyWithRetry(() => import('./Pages/EmbedShareCapture'));
const AerialSurveying = lazyWithRetry(() => import('./Pages/Verticals/AerialSurveying'));
const LocationVisualisation = lazyWithRetry(() => import('./Pages/Verticals/LocationVisualisation'));
const Ecommerce3D = lazyWithRetry(() => import('./Pages/Verticals/Ecommerce3D'));
const NotFound = lazyWithRetry(() => import('./Pages/NotFound/NotFound'));
const ShopifyApp = lazyWithRetry(() => import('./Pages/Shopify/ShopifyApp'));
const ShopifyLegacyRedirect = lazyWithRetry(() =>
  import('./Pages/Shopify/ShopifyEmbeddedStudio').then((mod) => ({ default: mod.ShopifyLegacyRedirect }))
);
const ShopifyBillingRoute = lazyWithRetry(() =>
  import('./Pages/Shopify/ShopifyEmbeddedStudio').then((mod) => ({ default: mod.ShopifyBillingRoute }))
);
const ShopifyCaptureRoute = lazyWithRetry(() =>
  import('./Pages/Shopify/ShopifyEmbeddedStudio').then((mod) => ({ default: mod.ShopifyCaptureRoute }))
);
const ShopifyProductsRoute = lazyWithRetry(() =>
  import('./Pages/Shopify/ShopifyEmbeddedStudio').then((mod) => ({ default: mod.ShopifyProductsRoute }))
);

/** Redirect /studio to /studio/:uid when logged in (bookmark support). */
function StudioRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const routing = user.workspaceRoutingUid || user.uid;
  return <Navigate to={`/studio/${routing}`} replace />;
}

/** Redirect /studio/:studioID → /studio/:studioID/mystudio */
function StudioIndexRedirect() {
  const { studioID } = useParams();
  return <Navigate to={`/studio/${studioID}/mystudio`} replace />;
}

/** Bookmark-only: old `/studio/:id/captures` URLs → mystudio / project room */
function StudioCapturesLegacyRedirect() {
  const { studioID, projectId } = useParams();
  if (projectId) {
    return <Navigate to={`/studio/${studioID}/${projectId}`} replace />;
  }
  return <Navigate to={`/studio/${studioID}/mystudio`} replace />;
}

/** Owner or invitee capture viewer at /studio/:uid/:projectId/:captureId (full viewport, no UserLayout chrome). */
function StudioCaptureRoute() {
  const { user } = useAuth();
  const { studioID } = useParams();
  if (!user) return <Navigate to="/login" replace />;
  const routing = user.workspaceRoutingUid || user.uid;
  const membershipOwners = Array.isArray(user.workspaceMembershipOwnerIds)
    ? user.workspaceMembershipOwnerIds
    : [];
  const canAccessStudio =
    studioID === routing || (studioID && membershipOwners.includes(studioID));
  if (!canAccessStudio) {
    return <Navigate to={`/studio/${routing}/mystudio`} replace />;
  }
  return (
    <StudioMetricsProvider
      studioUid={studioID === routing ? routing : null}
      enabled={studioID === routing}
      planStartedAtMs={user.planStartedAt}
    >
      <ProjectProvider>
        <CaptureViewer />
      </ProjectProvider>
    </StudioMetricsProvider>
  );
}

/** Visiting /contact opens the modal and redirects to home so the backdrop shows the page. */
function ContactOpenRedirect() {
  const navigate = useNavigate();
  const { openModal } = useContactModal();
  useEffect(() => {
    openModal();
    navigate('/', { replace: true });
  }, [openModal, navigate]);
  return null;
}

function AuthSessionOverlayHost() {
  const { authSessionOverlay } = useAuth();
  if (!authSessionOverlay) return null;
  return <VolugraphLoadingScreen overlay />;
}

const volugraphToastMark = (
  <span className="volugraph-toast-mark-circle" aria-hidden={true}>
    <img
      src={volugraphToastFavicon}
      alt=""
      className="volugraph-toast-mark volugraph-toast-mark--favicon"
      aria-hidden={true}
      draggable={false}
    />
  </span>
);

const volugraphToastLoadingIcon = (
  <span className="volugraph-toast-loading-icons" aria-hidden>
    {volugraphToastMark}
    <span className="custom-spinner" />
  </span>
);

function RouteSuspenseFallback() {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/shopify/')) {
    return <ShopifyLoadingScreen />;
  }
  return <VolugraphLoadingScreen />;
}

function App() {
  return (
    <HelmetProvider>
    <UploadProvider>
      <CookieConsentProvider>
      <Router>
        <ContactModalProvider>
          <div className="App">
            <AuthSessionOverlayHost />
            <CookieConsent />
            <SeoAnalytics />
            <AppErrorBoundary>
            <Suspense fallback={<RouteSuspenseFallback />}>
            <Routes>
            <Route path="/" element={<Landing />} />
          <Route path="/explore" element={<Navigate to={{ pathname: '/', hash: 'the-studio' }} replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/aerial-surveying" element={<AerialSurveying />} />
            <Route path="/location-visualisation" element={<LocationVisualisation />} />
            <Route path="/ecommerce-3d" element={<Ecommerce3D />} />
          <Route path="/guides/3dgs-vs-matterport" element={<Navigate to="/blog/aerial-footage-to-3d-gaussian-splat" replace />} />
          <Route path="/blog/3dgs-vs-matterport" element={<Navigate to="/blog/aerial-footage-to-3d-gaussian-splat" replace />} />
          <Route path="/guides/aerial-drone-capture" element={<Navigate to="/blog/e-commerce-products" replace />} />
          <Route path="/blog/aerial-drone-capture" element={<Navigate to="/blog/e-commerce-products" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/invite/:ownerUid/:projectId/:inviteId" element={<LazyInvite.AcceptProjectInvitePage />} />
          {/* Documentation (Learn) under marketing shell when logged out. */}
          <Route path="/" element={<UserLayout />}>
            <Route path="documentation" element={<DocumentationLayout />}>
              <Route index element={<DocumentationHome />} />
              <Route path="official-guides/:articleSlug" element={<DocumentationOfficialArticle />} />
              <Route path="getting-started" element={<Navigate to="/documentation/frequently-asked-questions" replace />} />
              <Route path="how-many-photos" element={<Navigate to="/documentation/the-first-project" replace />} />
              <Route path="capture-checklist" element={<Navigate to="/documentation/the-3d-workspace" replace />} />
              <Route path="quick-tip" element={<Navigate to="/documentation/geospatial-integration" replace />} />
              <Route path=":categorySlug/:articleSlug" element={<DocumentationArticle />} />
              <Route path=":categorySlug" element={<DocumentationCategoryPage />} />
            </Route>
            <Route path="blog" element={<LazyBlog.Index />} />
            <Route path="blog/:slug" element={<LazyBlog.Post />} />
          </Route>
            <Route path="/viewer" element={<CaptureViewer />} />
            <Route path="/share/:token" element={<ShareView />} />
            {/** Short share links from generateShareLink / hosting /s/** — same viewer as /share for local dev / SPA */}
            <Route path="/s/:token" element={<ShareView />} />
            <Route path="/embed/:captureId" element={<EmbedCapture />} />
            <Route path="/embed/s/:token" element={<EmbedShareCapture />} />
            <Route path="/shopify/app" element={<ShopifyApp />}>
              <Route path=":studioID/products" element={<ShopifyProductsRoute />} />
              <Route path=":studioID/billing" element={<ShopifyBillingRoute />} />
              <Route path=":studioID/:projectId/:captureId" element={<ShopifyCaptureRoute />} />
              <Route path=":studioID/mystudio" element={<ShopifyLegacyRedirect />} />
              <Route path=":studioID/:projectId" element={<ShopifyLegacyRedirect />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Outlet />}>
                <Route path="/studio/:studioID/:projectId/:captureId" element={<StudioCaptureRoute />} />
                <Route path="/" element={<UserLayout />}>
                  <Route path="studio" element={<StudioRedirect />} />
                  <Route path="studio/:studioID" element={<UserOutlet />}>
                    <Route index element={<StudioIndexRedirect />} />
                    <Route path="mystudio" element={<UserCaptures />} />
                    <Route path="captures" element={<StudioCapturesLegacyRedirect />} />
                    <Route path="captures/:projectId" element={<StudioCapturesLegacyRedirect />} />
                    <Route path="billing" element={<UserBilling />} />
                    <Route path="settings" element={<UserSettings />} />
                    {/* Map workspace must be before `:projectId` so `/map` is not parsed as a project id. */}
                    <Route path=":projectId/map" element={<UserCaptures />} />
                    <Route path=":projectId" element={<UserCaptures />} />
                  </Route>
                </Route>
              </Route>
            </Route>
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<ContactOpenRedirect />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
            </Suspense>
            </AppErrorBoundary>
          <ContactModal />
          <Toaster
            position="top-center"
            containerStyle={{
              zIndex: 99999,
            }}
            toastOptions={{
              duration: 5000,
              className: 'volugraph-toast',
              icon: volugraphToastMark,
              style: {
                background: '#1b344b',
                color: '#fff',
                borderRadius: '12px',
                padding: '14px 18px',
                fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                lineHeight: 1.4,
                boxShadow: '0 10px 28px rgba(0, 0, 0, 0.22)',
                maxWidth: 'min(560px, calc(100vw - 2rem))',
                width: 'auto',
                maxHeight: 'none',
                overflow: 'visible',
                alignItems: 'center',
              },
              loading: {
                icon: volugraphToastLoadingIcon,
                duration: Infinity,
              },
              success: {
                icon: volugraphToastMark,
              },
              error: {
                icon: volugraphToastMark,
              },
            }}
          />
        </div>
        </ContactModalProvider>
      </Router>
      </CookieConsentProvider>
    </UploadProvider>
    </HelmetProvider>
  );
}

export default App;
